import React, { useState, useContext, useRef } from 'react';
import axiosInstance from '../axiosConfig';
import { AuthContext } from '../AuthContext';
import {
  Form, Button, Alert, Spinner, Container, Row, Col, ProgressBar, Card,
} from 'react-bootstrap';
import {
  FaUpload, FaFileExcel, FaCheckCircle, FaExclamationCircle, FaCopy,
} from 'react-icons/fa';
import './FileUploadComponent.css';

const FileUploadComponent = () => {
  const [file, setFile] = useState(null);
  const [actFile, setActFile] = useState(null);
  const [operationType, setOperationType] = useState('mass_creation');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [batchNumbersNotFound, setBatchNumbersNotFound] = useState([]);

  const {
    authTokens, setProjects, selectedServiceCenter,
  } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  const actFileInputRef = useRef(null);
  const notFoundRef = useRef(null);

  const fetchProjectsclone = async () => {
    if (!selectedServiceCenter?.id) {
      console.warn('selectedServiceCenter.id отсутствует');
      setMessage({ type: 'danger', text: 'Не выбран сервисный центр.' });
      return;
    }

    try {
      const response = await axiosInstance.get('/api/projects/', {
        params: { service_center: selectedServiceCenter.id },
        headers: {
          'Authorization': `Bearer ${authTokens.access}`,
        },
      });
      if (response.status === 200) {
        setProjects(response.data.projects || []);
        setMessage({ type: 'success', text: 'Проекты успешно обновлены.' });
      } else {
        setMessage({ type: 'danger', text: 'Не удалось обновить проекты.' });
      }
    } catch (error) {
      console.error('Ошибка при загрузке проектов:', error);
      setMessage({ type: 'danger', text: 'Ошибка при загрузке проектов.' });
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setMessage({ type: '', text: '' });
    setUploadProgress(0);
  };

  const handleActFileChange = (e) => {
    setActFile(e.target.files[0]);
  };

  const handleOperationTypeChange = (e) => {
    setOperationType(e.target.value);
    setFile(null);
    setActFile(null);
    setBatchNumbersNotFound([]);
    if (fileInputRef.current) fileInputRef.current.value = null;
    if (actFileInputRef.current) actFileInputRef.current.value = null;
  };

  const copyToClipboard = () => {
    const textToCopy = batchNumbersNotFound.join('\n');
    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        window.Toastify({
          text: 'Партийные номера скопированы в буфер обмена.',
          duration: 3000,
          gravity: 'top',
          position: 'right',
          backgroundColor: '#28a745',
        }).showToast();
      })
      .catch((err) => {
        console.error('Ошибка при копировании: ', err);
      });
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file) {
      setMessage({ type: 'danger', text: 'Пожалуйста, выберите файл.' });
      return;
    }

    if (operationType === 'mass_receipt' && !actFile) {
      setMessage({ type: 'danger', text: 'Пожалуйста, загрузите акт.' });
      return;
    }

    if (!selectedServiceCenter?.id) {
      setMessage({ type: 'danger', text: 'Не выбран сервисный центр.' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('operation_type', operationType);
    formData.append('service_center_id', selectedServiceCenter.id);  // Добавляем service_center_id
    if (operationType === 'mass_receipt' && actFile) {
      formData.append('act_file', actFile);
    }

    try {
      const response = await axiosInstance.post(
        '/api/upload-components/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authTokens.access}`,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            setUploadProgress(percentCompleted);
          },
        },
      );

      if (response.data.success) {
        let successText = '';
        if (operationType === 'mass_creation') {
          successText = 'Компоненты успешно созданы.';
        } else if (operationType === 'mass_receipt') {
          successText = 'Компоненты успешно оприходованы.';
        } else {
          successText = 'Файл успешно загружен и данные обработаны.';
        }

        if (
          operationType === 'mass_receipt' &&
          response.data.batch_numbers_not_found &&
          response.data.batch_numbers_not_found.length > 0
        ) {
          setBatchNumbersNotFound(response.data.batch_numbers_not_found);
          // Скроллим к не найденным номерам
          setTimeout(() => {
            if (notFoundRef.current) {
              notFoundRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        } else {
          setBatchNumbersNotFound([]);
        }
        setMessage({ type: 'success', text: successText });
        await fetchProjectsclone();
      } else {
        setMessage({
          type: 'danger',
          text: 'Произошла ошибка при обработке файла.',
        });
      }
    } catch (error) {
      console.error('Ошибка при загрузке файла:', error);
      if (error.response) {
        setMessage({
          type: 'danger',
          text: `Ошибка при загрузке файла: ${
            error.response.data.message || error.response.statusText
          }`,
        });
      } else {
        setMessage({ type: 'danger', text: 'Ошибка при загрузке файла.' });
      }
    } finally {
      setLoading(false);
      setFile(null);
      setActFile(null);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = null;
      if (actFileInputRef.current) actFileInputRef.current.value = null;
    }
  };

  // Функция для получения ссылки на шаблон в зависимости от типа операции
  const getTemplateLink = () => {
    if (operationType === 'mass_creation') {
      return '/Шаблон создания.xlsx';
    } else if (operationType === 'mass_receipt') {
      return '/Шаблон оприходования.xlsx';
    }
    return '#';
  };

  // Получение названия шаблона для отображения
  const getTemplateName = () => {
    if (operationType === 'mass_creation') {
      return 'Шаблон создания.xlsx';
    } else if (operationType === 'mass_receipt') {
      return 'Шаблон оприходования.xlsx';
    }
    return '';
  };

  return (
    <Container className="mt-5 download upload-shell">
      <Row className="justify-content-md-center">
        <Col xs={12} md={8} lg={6}>
          <Card className="shadow-sm upload-card">
            <Card.Body className="upload-card-body">
              <div className="text-center mb-4 upload-header">
                <FaUpload size={50} color="#0d6efd" />
                <h3 className="mt-3 upload-title">Массовая загрузка компонентов</h3>
                <p className="text-muted upload-subtitle">
                  Загрузите Excel файл для массового добавления компонентов
                </p>
              </div>
              <Form onSubmit={handleUpload}>
                <Form.Group controlId="formOperationType" className="mb-4">
                  <Form.Label>Выберите тип операции</Form.Label>
                  <Form.Control
                    as="select"
                    value={operationType}
                    onChange={handleOperationTypeChange}
                    disabled={loading}
                  >
                    <option value="mass_creation">Массовое создание</option>
                    <option value="mass_receipt">Массовое оприходование</option>
                  </Form.Control>
                </Form.Group>

                {/* Добавляем ссылку на шаблон */}
                <div className="mb-4 upload-template-link">
                  <a href={getTemplateLink()} download>
                    <FaFileExcel className="me-2" />
                    Скачать {getTemplateName()}
                  </a>
                </div>

                <Form.Group controlId="formFile" className="mb-4">
                  <Form.Label>
                    Выберите файл <FaFileExcel className="ml-2" />
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    disabled={loading}
                    ref={fileInputRef}
                    className="custom-file-input"
                  />
                  {file && (
                    <Form.Text className="selected-file-text mt-2">
                      Выбранный файл: <strong>{file.name}</strong>
                    </Form.Text>
                  )}
                  <Form.Text className="text-muted upload-help-text">
                    Поддерживаемые форматы: .xlsx, .xls
                  </Form.Text>
                </Form.Group>

                {operationType === 'mass_receipt' && (
                  <Form.Group controlId="formActFile" className="mb-4">
                    <Form.Label>Загрузите акт оприходования (PDF)</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".pdf"
                      onChange={handleActFileChange}
                      disabled={loading}
                      ref={actFileInputRef}
                      className="custom-file-input"
                    />
                    {actFile && (
                      <Form.Text className="selected-file-text mt-2">
                        Выбранный акт: <strong>{actFile.name}</strong>
                      </Form.Text>
                    )}
                    <Form.Text className="text-muted upload-help-text">
                      Поддерживаемый формат: .pdf
                    </Form.Text>
                  </Form.Group>
                )}

                {loading && (
                  <div className="progress-container mb-4">
                    <ProgressBar
                      now={uploadProgress}
                      label={`${uploadProgress}%`}
                      className="mb-2 progress-bar-custom"
                      animated
                      striped
                    />
                    <div className="progress-info">
                      <span>Загрузка: {uploadProgress}%</span>
                      <Spinner animation="border" size="sm" className="ms-2" />
                    </div>
                  </div>
                )}
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading || !file}
                  className="w-100 btn-custom"
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />{' '}
                      Загрузка...
                    </>
                  ) : (
                    'Загрузить файл'
                  )}
                </Button>
              </Form>
              {message.text && (
                <>
                  <Alert
                    variant={message.type}
                    className={`mt-4 alert-custom ${
                      message.type === 'success'
                        ? 'alert-success-custom'
                        : 'alert-danger-custom'
                    }`}
                  >
                    {message.type === 'success' ? (
                      <FaCheckCircle className="me-2" />
                    ) : (
                      <FaExclamationCircle className="me-2" />
                    )}
                    {message.text}
                  </Alert>
                  {operationType === 'mass_receipt' &&
                    batchNumbersNotFound.length > 0 && (
                      <Alert variant="warning" className="mt-2" ref={notFoundRef}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <FaExclamationCircle className="me-2" />
                            <strong>Не найдены следующие партийные номера:</strong>
                          </div>
                          <Button variant="outline-secondary" className='text-dark' size="sm" onClick={copyToClipboard}>
                            <FaCopy className="me-1 " /> Копировать все
                          </Button>
                        </div>
                        <ul className="text-dark">
                          {batchNumbersNotFound.map((batchNumber) => (
                            <li key={batchNumber}>{batchNumber}</li>
                          ))}
                        </ul>
                      </Alert>
                    )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default FileUploadComponent;
